import { useCallback, useEffect, useState } from 'react';
import type {
  FleetBoardRun,
  FleetJobEventView,
  FleetLogLineStream,
  FleetLogReadLimit,
  ReadFleetLogResponse,
} from '@immo/api-contract';
import { apiClient } from '@/lib/auth/api-client';
import { useTranslations } from '@/i18n/use-translations';
import { JobMessages } from '@/components/fleet/job-messages';
import { JobTimeline } from '@/components/fleet/job-timeline';
import { LogViewer } from '@/components/fleet/log-viewer';

const DEFAULT_STREAM: FleetLogLineStream = 'agent';
const DEFAULT_LIMIT: FleetLogReadLimit = 50;

type LogState = {
  readonly stream: FleetLogLineStream;
  readonly limit: FleetLogReadLimit;
  readonly log: ReadFleetLogResponse | null;
  readonly loading: boolean;
};

const NO_LOG: LogState = { stream: DEFAULT_STREAM, limit: DEFAULT_LIMIT, log: null, loading: false };

type Props = {
  readonly run: FleetBoardRun;
  readonly canCommand: boolean;
  /** Changes whenever the board polled: a live run's timeline and log are re-read with it. */
  readonly refreshKey: unknown;
};

/** The selected run's messages, timeline and log: what the jobs page shows for one job, for a past run too. */
export function RunDetail({ run, canCommand, refreshKey }: Props) {
  const t = useTranslations('pages.admin.jobs');
  const { jobId, machineId, worker } = run;
  const live = run.endedAt === null;
  const [timeline, setTimeline] = useState<{ jobId: string; events: ReadonlyArray<FleetJobEventView> | null }>({
    jobId,
    events: null,
  });
  const [loadingTimeline, setLoadingTimeline] = useState(false);
  const [logs, setLogs] = useState<LogState>(NO_LOG);

  // The previous lines stay on screen while a re-read is in flight, so the list never flashes.
  useEffect(() => {
    let cancelled = false;

    setLoadingTimeline(true);
    void apiClient.adminFleetJobs.events({ pathParams: { jobId } }).then((result) => {
      if (cancelled) return;

      setLoadingTimeline(false);
      setTimeline((previous) => ({
        jobId,
        events: result.ok ? result.body.data : previous.jobId === jobId ? previous.events : [],
      }));
    });

    return () => {
      cancelled = true;
    };
  }, [jobId, refreshKey]);

  const readLog = useCallback(
    async (stream: FleetLogLineStream, limit: FleetLogReadLimit) => {
      setLogs((previous) => ({ ...previous, stream, limit, loading: true }));

      const result = await apiClient.adminFleetLogs.read({
        pathParams: { machineId },
        query: { worker, stream, limit, jobId },
      });

      setLogs((previous) => ({ ...previous, loading: false, ...(result.ok ? { log: result.body } : {}) }));
    },
    [machineId, worker, jobId],
  );

  // The log belongs to the selected run: dropped when the selection moves, never carried across from another run.
  useEffect(() => {
    setLogs(NO_LOG);
    void readLog(DEFAULT_STREAM, DEFAULT_LIMIT);
  }, [readLog]);

  return (
    <div className="flex flex-col gap-5">
      <JobMessages jobId={jobId} canCommand={canCommand && live} />

      <section className="flex flex-col gap-2">
        <h3 className="text-sm font-semibold text-text-primary">{t('timeline-heading')}</h3>
        <JobTimeline events={timeline.jobId === jobId ? timeline.events : null} loading={loadingTimeline} />
      </section>

      <section className="flex flex-col gap-2">
        <h3 className="text-sm font-semibold text-text-primary">{t('logs-heading')}</h3>
        <LogViewer
          readOnly
          stream={logs.stream}
          limit={logs.limit}
          log={logs.log}
          loading={logs.loading}
          onStreamChange={(stream) => void readLog(stream, logs.limit)}
          onLimitChange={(limit) => void readLog(logs.stream, limit)}
          onRefresh={() => void readLog(logs.stream, logs.limit)}
        />
      </section>
    </div>
  );
}
